<template>
    <div class="doctor-component">
        <card :title="embedded ? '' : 'Doctor Record'" :plain="embedded">
            <template #header-right>
                <history-modal v-model="doctor" />
            </template>
            <form>
                <b-row v-if="newMode">
                    <b-col>
                        <search-component v-model="surescriptsDoctor" label="NPI Lookup"
                            search-u-r-l="/api/Prescriber/Surescripts" clearable :disabled="disableFields"
                            :option-label="surescriptsOptionLabel" @input="surescriptsSelected" />
                    </b-col>
                </b-row>
                <NameSearchComponent v-model="displayName" :named-person="doctor" :disable-fields="disableFields"
                    :searchMode="disableFields" @person-update="personUpdate" @input="nameChanged">
                    <div style="min-width: 250px;">
                        <fg-input v-model="doctor.phoneNumber" label="Phone" :disabled="disableFields" />
                    </div>
                </NameSearchComponent>
                <b-form-row>
                    <b-col>
                        <AddressFormFields v-model="doctor" :disable-fields="disableFields">
                            <!--<template #row-1>
                                <b-col cols="4" offset="1">
                                    <fg-input v-model="doctor.cellNumber"
                                              label="Cell"
                                              :disabled="disableFields"></fg-input>
                                </b-col>
                            </template>-->
                            <template #row-1>
                                <b-col cols="4" offset="1">
                                    <fg-input v-model="doctor.altNumber" label="Alternate Phone"
                                        :disabled="disableFields" />
                                </b-col>
                            </template>
                            <template #row-2>
                                <b-col cols="4" offset="1">
                                    <b-row cols="1" cols-xl="2">
                                        <b-col>
                                            <fg-input v-model="doctor.faxNumber" label="Fax"
                                                :disabled="disableFields" />
                                        </b-col>
                                        <b-col>
                                            <fg-input v-model="doctor.altFaxNumber" label="Alternate Fax"
                                                :disabled="disableFields" />
                                        </b-col>
                                    </b-row>
                                </b-col>
                            </template>
                        </AddressFormFields>
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col cols="7">
                        <fg-input v-model="doctor.emailAddress" label="Email" type="email" :disabled="disableFields" />
                    </b-col>
                    <b-col>
                        <fg-input v-model="doctor.practiceName" label="Practice Name" :disabled="disableFields" />
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col>
                        <fg-input v-model="doctor.deaNumber" label="DEA Number" :disabled="disableFields" />
                    </b-col>
                    <b-col>
                        <fg-input v-model="doctor.npiNumber" label="NPI Number" :disabled="disableFields"
                            :required="requiresNPI" />
                    </b-col>
                    <b-col>
                        <fg-input v-model="doctor.stateLicenseNumber" label="State License Number"
                            :disabled="disableFields" />
                    </b-col>
                    <b-col>
                        <fg-input v-model="doctor.sureScriptsId" label="Surescripts ID" :disabled="disableFields" />
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col>
                        <fg-input v-model="doctor.medicaidNumber" label="Medicaid Number" :disabled="disableFields"
                            maxlength="40" />
                    </b-col>
                    <b-col>
                        <EnumSelector v-model="doctor.communicationPreference" :disabled="disableFields"
                            :enum-codes="PrescriberCommunicationPreference" label="Communication Preference"
                            clearable />
                    </b-col>
                    <b-col>
                        <label>Specialities:</label>
                        <Multiselect v-model="doctor.specialities" :options="specialities" track-by="id" label="name"
                            multiple :disabled="disableFields" />
                    </b-col>
                    <b-col></b-col>
                </b-form-row>
                <b-form-row>
                    <b-col>
                        <b-form-group label="Hours of Operation">
                            <b-textarea v-model="doctor.hoursOfOperation" :disabled="disableFields" />
                        </b-form-group>
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col cols="12">
                        <fg-input label="Alert">
                            <b-textarea v-model="doctor.alert" :disabled="disableFields" />
                        </fg-input>
                    </b-col>
                    <b-col class="d-flex" style="gap: 10px;">
                        <b-checkbox v-model="doctor.allowsPa" :disabled="disableFields">
                            Allows Pa
                        </b-checkbox>
                        <b-checkbox v-model="doctor.reminderOptOut" :disabled="disableFields">
                            Fax Reminder Opt Out
                        </b-checkbox>
                    </b-col>
                </b-form-row>
                <b-row>
                    <b-col>
                        <SearchComponent v-model="selectedProgram" label="Program" search-u-r-l="/Program"
                            :params="{ active: true }" :disabled="disableFields"
                            :id="selectedProgram && selectedProgram.id" clearable prepopulate />
                    </b-col>
                </b-row>
                <hr>
                <b-row v-if="doctor && doctor.id">
                    <b-col>
                        <NotesForm :entityId="doctor.id" :entityType="doctorEntityType" @saved="refreshNotesList" />
                        <NotesList ref="notesList" :entityId="doctor.id" :entityType="doctorEntityType" :pageSize="5" />
                    </b-col>
                </b-row>
            </form>
            <div v-if="doctor?.contacts">
                <h4>Office Contacts</h4>

                <div class="contact-cards">
                    <b-card v-for="(contact, index) in doctor.contacts" :key="contact.id">
                        <div class="pull-right">
                            <b-button variant="link" @click.prevent="editContact(contact, index)" title="Edit"
                                class="m-0">
                                <b-icon icon="pencil" variant="primary" />
                            </b-button>
                            <b-button variant="link" @click.prevent="removeContact(contact)" title="Remove" class="m-0">
                                <b-icon icon="x-circle-fill" variant="danger" />
                            </b-button>
                        </div>
                        <strong>
                            {{ contact.firstName }}
                            {{ contact.lastName }}
                        </strong>
                        <div>
                            {{ contact.title }}
                        </div>
                        <div>
                            {{ contact.phoneNumber }}
                        </div>
                        <div>
                            <small class="text-muted">
                                {{ contact.note }}
                            </small>
                        </div>
                    </b-card>

                    <b-card body-class="text-center" @click="addContact" class="add-card text-muted">
                        <div>
                            <i class="fa fa-3x fa-plus-circle mb-3" />
                        </div>

                        Add Office Contact
                    </b-card>
                </div>
            </div>
            <NewClearSaveButtons :titles="buttonTitles" :disable="disableFields" :disable-save="!isFormValid"
                :disable-clear="disableFields" @new-clicked="createPrescriber" @clear-clicked="undoChanges"
                @save-clicked="savePrescriber" @cancel-clicked="emitClose" />
        </card>

        <b-modal id="office-contact-modal" title="Office Contact" centered no-fade
            :ok-disabled="!selectedContactIsValid" @ok="updateContact">
            <template v-if="selectedContact">
                <b-form-group label="First Name *">
                    <b-input v-model="selectedContact.firstName" maxlength="35" autofocus />
                </b-form-group>
                <b-form-group label="Last Name *">
                    <b-input v-model="selectedContact.lastName" maxlength="45" />
                </b-form-group>
                <b-form-group label="Title">
                    <b-input v-model="selectedContact.title" maxlength="25" />
                </b-form-group>
                <b-form-group label="Phone Number">
                    <b-input v-model="selectedContact.phoneNumber" maxlength="12" />
                </b-form-group>
                <b-form-group label="Note">
                    <b-textarea v-model="selectedContact.note" maxlength="150" />
                </b-form-group>
            </template>
        </b-modal>
    </div>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import DisableAutocompleteMixin from '@/mixins/DisableAutocompleteMixin';
import axios, { AxiosError } from 'axios';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { Lock, LockHandler } from '@/mixins/LockHandler';
import { Prescriber, PrescriberCommunicationPreference, PrescriberContact } from '@/models/Prescriber';
import { Program } from '@/models/Program';
import NameSearchComponent from '@/components/NameSearchComponent.vue';
import NewClearSaveButtons from '@/components/NewClearSaveButtons.vue';
import AddressFormFields from '@/components/AddressFormFields.vue';
import SearchComponent from '@/components/SearchComponent.vue';
import NotesList from '../Note/NotesList.vue';
import NotesForm from '../Note/NotesForm.vue';
import { EntityType } from "@/models/EntityType";
import NotificationTypes = NotificationOptions.NotificationTypes;
import HistoryModal from "@/components/Modals/HistoryModal.vue";
import EnumSelector from '@/components/EnumSelector.vue';
import moment from "moment";
import Multiselect from 'vue-multiselect';
import { Speciality } from '@/models/Speciality';

@Component({
    name: 'DoctorFormComponent',
    components: {
        HistoryModal,
        NameSearchComponent,
        NewClearSaveButtons,
        AddressFormFields,
        SearchComponent,
        NotesForm,
        NotesList,
        EnumSelector,
        Multiselect
    },
})
export default class DoctorFormComponent extends Mixins(LockHandler, DisableAutocompleteMixin) {

    @Prop() private buttonTitles!: any;
    @Prop({ type: Boolean, default: false }) private createNewDoctor!: boolean;
    @Prop() private escriptResponsePrescriber?: Prescriber;
    @Prop() private prescriberId?: number;
    @Prop({ default: false }) private embedded!: boolean;

    created() {
        console.log(`%c Created ${this.$options.name}`, "color: green");
        if (this.createNewDoctor)
            this.createPrescriber();
        if (this.prescriberId) {
            this.fetchPrescriber({ id: this.prescriberId } as Prescriber);
        }
        this.fetchSpecialties();
    }

    @Watch("prescriberId")
    onPrescriberIdChanged(value: number, oldValue: number) {
        if (value === oldValue) return;

        if (value) {
            this.fetchPrescriber({ id: value } as Prescriber);
        } else {
            this.createPrescriber();
        }
    }

    beforeDestroy() {
        console.log(`%c Destroying ${this.$options.name}`, "color: red");
    }

    private surescriptsDoctor: Prescriber = new Prescriber();
    private doctor: Prescriber = new Prescriber();
    private selectedProgram: Program = new Program();

    private doctorLock: Lock | null = null;

    private newMode = false;
    private doctorEntityType = EntityType.Prescriber;
    protected specialities: Speciality[] = [];

    PrescriberCommunicationPreference = PrescriberCommunicationPreference;

    refreshNotesList() {
        if (this.$refs.notesList instanceof NotesList)
            this.$refs.notesList.loadNotes();
    }

    get disableFields(): boolean {
        const lockedForUser = !this.doctorLock;
        const noEntityLoaded = !(this.doctor && this.doctor.id);

        // Disable fields if
        // ** its locked for the user
        // ** or if there is no doctor loaded and is not in New object Mode.
        return (lockedForUser || noEntityLoaded) && !this.newMode;
    }

    private nameIntroduced = false;
    private displayName = { firstName: "", middleName: "", lastName: "" };

    @Watch('doctor') onDoctorChanged(value: Prescriber, oldValue: Prescriber) {
        if (!value) {
            this.displayName = { firstName: "", middleName: "", lastName: "" };
            return;
        }
        const doctor = value;

        if (!value.id) {
            doctor.firstName = this.displayName.firstName;
            doctor.middleName = this.displayName.middleName;
            doctor.lastName = this.displayName.lastName;
        } else {
            if (!oldValue || oldValue.id != doctor.id) {
                this.fetchPrescriber(doctor);
            }
        }

        this.displayName = {
            firstName: doctor.firstName,
            middleName: doctor.middleName,
            lastName: doctor.lastName
        };
    }

    async obtainLockOnPrescriber(prescriber: Prescriber) {
        try {
            const result = await this.addLockWithURL(`/Prescriber/${prescriber.id}/lock`, 60000);
            this.doctorLock = result;
        } catch (err) {
            const error = err as AxiosError;
            if (error.response?.status == 418) {
                // doctor locked
                console.warn('Doctor is locked', error.response);
                this.showLockedMessage(
                    EntityType.Prescriber,
                    prescriber?.id.toString(),
                    prescriber?.displayNamesForPerson(),
                    error.response.data
                );
            } else {
                this.$notification(NotificationOptions.error(err));
            }
        }
    }

    async fetchPrescriber(prescriber: Prescriber) {
        await this.obtainLockOnPrescriber(prescriber);

        try {
            const response = await axios.get(`/Prescriber/${prescriber.id}`);

            this.doctor = Object.assign(new Prescriber(), response.data);
            this.selectedProgram.id = this.doctor?.programId || 0;
            this.displayName = {
                firstName: this.doctor.firstName,
                middleName: this.doctor.middleName,
                lastName: this.doctor.lastName
            }
            this.nameIntroduced = !!this.doctor.firstName && !!this.doctor.lastName;
            this.newMode = false;
        } catch (error) {
            console.error(error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    surescriptsSelected(value: any) {
        this.doctor = new Prescriber(value);
        (this.doctor.id as any) = undefined;
        this.displayName = {
            firstName: this.doctor.firstName,
            middleName: this.doctor.middleName,
            lastName: this.doctor.lastName
        };
        this.nameIntroduced = !!this.doctor.firstName && !!this.doctor.lastName;
    }

    personUpdate(value: any) {
        this.doctor = value;
        this.$router.replace('/maintenance/doctor/' + this.doctor.id);
    }

    nameChanged(val: any) {
        this.nameIntroduced = !!val.firstName && !!val.lastName;
    }

    async undoChanges() {
        try {
            const value = await this.$bvModal.msgBoxConfirm('Are you sure you want to clear?', {
                title: 'Confirm',
                okVariant: 'danger',
                centered: true,
                noFade: true
            });

            if (!value) return;

            if (this.doctorLock && this.doctorLock.refreshURL) {
                this.releaseLockAtURL(this.doctorLock.refreshURL);
                this.doctorLock = null;
            }
            this.doctor = new Prescriber();
            this.newMode = false;
            this.selectedProgram = new Program();
            this.displayName = { lastName: "", firstName: "", middleName: "" };
            this.$router.push({ name: 'Doctor' });
        } catch (error) {
            console.error("Error caught in undoChanges()", error);
        }
    }

    createPrescriber() {
        this.newMode = true;
        this.displayName = { lastName: "", firstName: "", middleName: "" };
        this.doctor = new Prescriber();
        if (this.escriptResponsePrescriber) {
            Object.assign(this.doctor, this.escriptResponsePrescriber);
            this.displayName = {
                firstName: this.doctor.firstName,
                middleName: this.doctor.middleName,
                lastName: this.doctor.lastName
            };
            this.nameIntroduced = !!this.doctor.firstName && !!this.doctor.lastName;
        }
        window.scrollTo(0, 0);
    }

    checkNpiNumber() {
        const checkString = "80840" + this.doctor.npiNumber.substring(0, 9);
        const digits = checkString.split("").reverse().join("");
        let s = 0;
        let t = 0;
        for (let i = 0; i < digits.length; i++) {
            const digit = Number(digits[i]);
            if (i % 2 == 0) {
                t = 2 * digit;
                if (t > 9) t -= 9;
                s += t;
            } else {
                s += digit;
            }
        }
        const calculatedChecksum = (s * 9) % 10;
        const checkNum = Number(this.doctor.npiNumber) % 10;

        return calculatedChecksum === checkNum;

    }

    get requiresNPI() {
        return !this.doctor?.specialities?.length ||
            this.doctor?.specialities?.some(s => s.requiresNPI);
    }

    validate() {
        if (!this.requiresNPI) {
            return true;
        }

        if ((this.doctor?.npiNumber?.length ?? 0) != 10) {
            this.$notification(NotificationOptions.error("10 Digit NPI Number Required"));
            return false;
        }

        if (!this.checkNpiNumber()) {
            this.$notification(NotificationOptions.error("A Valid NPI Number must be entered."));
            return false;
        }

        return true;
    }

    async savePrescriber() {
        if (!this.doctor) {
            console.warn("We have no doctor/prescriber to save, get a new one?");
            this.createPrescriber();
        }

        if (!this.validate()) return;

        const doctor: Prescriber = this.doctor as Prescriber;

        doctor.firstName = this.displayName.firstName;
        doctor.lastName = this.displayName.lastName;
        doctor.middleName = this.displayName.middleName;

        const data = doctor;
        data.programId = (this?.selectedProgram?.id as number);

        try {
            const response = await axios.post<{ prescriber: Prescriber, warnings: Array<string> }>('/Prescriber', data);

            this.doctor = Object.assign(new Prescriber(), response.data.prescriber);

            const warnings = response.data.warnings;
            if (warnings.length) {
                this.$notification(NotificationOptions.notificationOptionsPreset(`Doctor saved with warning: ${warnings.join(", ")}`, NotificationTypes.warning));
            } else {
                this.$notification(NotificationOptions.successSaveNotificationPreset('Doctor'));
            }

            if (this.createNewDoctor) {
                this.$emit("created", this.doctor);
            }
            this.$emit("saved", this.doctor);
        } catch (error) {
            console.error(error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    get selectedProgramId() {
        return this.selectedProgram?.id || 0;
    }

    optionLabel(option: Prescriber) {
        return `ID: ${option.id} ${option.lastName}, ${option.firstName}`;
    }

    surescriptsOptionLabel(option: Prescriber) {
        if (!option) return '';

        let label = '';

        // Add name
        if (option.lastName && option.firstName) {
            label = `${option.lastName}, ${option.firstName}`;
        } else if (option.lastName) {
            label = option.lastName;
        } else if (option.firstName) {
            label = option.firstName;
        }

        // Add practice name if available
        if (option.practiceName) {
            label += ` - ${option.practiceName}`;
        }

        // Add address if available
        if (option.fullAddress) {
            label += ` - ${option.fullAddress}`;
        }

        // Add phone number if available
        if (option.phoneNumber) {
            label += ` - ${this.formatPhoneNumber(option.phoneNumber)}`;
        }

        return label || 'Unknown Prescriber';
    }

    get isFormValid() {
        return !!(this.nameIntroduced);
    }

    programLabel(object: Program) {
        return object.name ? `Program - Abbr: ${object.name} - ${object.abbreviation}` : '';
    }

    emitClose() {
        this.$emit("close");
    }

    removeContact(contact: PrescriberContact) {
        this.doctor.contacts = this.doctor.contacts.filter(c => c !== contact);
    }

    private selectedContact: PrescriberContact | null = null;
    private selectedContactIndex: number = -1;

    editContact(contact: PrescriberContact, index: number) {
        this.selectedContact = structuredClone(contact);
        this.selectedContactIndex = index;
        this.$bvModal.show("office-contact-modal");
    }

    updateContact() {
        this.doctor.contacts.splice(this.selectedContactIndex, 1, this.selectedContact!);
    }

    addContact() {
        this.selectedContact = new PrescriberContact();
        this.selectedContactIndex = this.doctor.contacts.length;
        this.$bvModal.show("office-contact-modal");
    }

    get selectedContactIsValid() {
        if (!this.selectedContact) return false;
        if (!this.selectedContact.lastName?.trim()) return false;
        if (!this.selectedContact.firstName?.trim()) return false;
        return true;
    }

    async fetchSpecialties() {
        try {
            const resp = await axios.get<Speciality[]>("/api/prescriber/specialties");
            this.specialities = resp.data;
        } catch (err) {
            console.error(err);
        }
    }

    formatPhoneNumber(value: string): string {
        if (!value) return "";
        value = value.replace("+1", "");
        if (value.length >= 10) {
            return value.substr(0, 3) + '-' +
                value.substr(3, 3) + '-' +
                value.substr(6, 4);
        }
        return value;
    }

} // close export of class
</script>

<style scoped>
.contact-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
    gap: 15px;
    margin-top: 10px;
}

.add-card {
    cursor: pointer;
    transition: all .2s;
}

.add-card:hover {
    color: black !important;
}
</style>
